import { backofficeWhoami } from "@/api/query/auth/auth";
import { ApiError } from "@/shared/api-fetch";
import { useSession } from "./session";

let inFlight: Promise<RefreshOutcome> | null = null;

/** What re-asking `/v1/me` found. `inactive`: the account itself is off — the session is over. */
export type RefreshOutcome = "unchanged" | "changed" | "inactive" | "failed";

/**
 * Re-resolves role and Casino from `GET /v1/me` — `useAuth().refresh()`'s job, for a caller with
 * no component in scope. `apiFetch` announces `ROLE_FORBIDDEN`/`STAFF_INACTIVE` from ANY request
 * (`setIdentityStaleHandler`), and `app/session-guard.ts` calls this: CLAUDE.md's "re-fetch
 * `/v1/me` after any surprising 403", done once, centrally, instead of per screen. Uses the plain
 * generated function (as `rehydrate.ts` does), since Vue Query hooks need component setup scope.
 *
 * Collapses concurrent calls into one request — several panels can 403 at once — which also
 * keeps a `STAFF_INACTIVE` from `/v1/me` itself from re-triggering this while it runs.
 */
export function refreshIdentity(): Promise<RefreshOutcome> {
  if (inFlight) return inFlight;
  const session = useSession();
  if (!session.isAuthed) return Promise.resolve("unchanged");
  const before = session.staff;
  inFlight = backofficeWhoami()
    .then((me): RefreshOutcome => {
      session.adopt({
        id: me.staffId,
        username: me.username,
        role: me.role,
        casinoId: me.casinoId,
      });
      return before?.role === me.role && before?.casinoId === me.casinoId ? "unchanged" : "changed";
    })
    .catch((err: unknown): RefreshOutcome => {
      if (err instanceof ApiError && err.code === "STAFF_INACTIVE") return "inactive";
      return "failed";
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}
